import { type ReactNode, createContext, useContext, useEffect, useMemo } from "react";

import { type Locale, localeHtmlLang } from "./locales";

import { getMessages } from "./messages";

import type { Messages } from "./messages/types";

import { localizedPath as buildLocalizedPath, localeCookieHeader } from "./server";

import { type MessageKey, createTranslator } from "./translate";

interface I18nContextValue {
	locale: Locale;

	messages: Messages;

	t: (key: MessageKey, params?: Record<string, string | number>) => string;

	localizedPath: (path: string) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

interface I18nProviderProps {
	locale: Locale;

	children: ReactNode;
}

export function I18nProvider({ locale, children }: I18nProviderProps) {
	const messages = useMemo(() => getMessages(locale), [locale]);

	const t = useMemo(() => createTranslator(messages), [messages]);

	useEffect(() => {
		document.documentElement.lang = localeHtmlLang(locale);

		document.cookie = localeCookieHeader(locale);
	}, [locale]);

	const value = useMemo<I18nContextValue>(
		() => ({
			locale,

			messages,

			t,

			localizedPath: path => buildLocalizedPath(path),
		}),

		[locale, messages, t],
	);

	return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
	const context = useContext(I18nContext);

	if (!context) {
		throw new Error("useI18n must be used within I18nProvider");
	}

	return context;
}

export function useTranslation() {
	const { t, locale, localizedPath, messages } = useI18n();

	return { t, locale, localizedPath, messages };
}
